import { effectiveMode } from './binding-mode';
import {
  categorySelection,
  fallbackHealth,
  groupByCategory,
  integrationKey,
  type CategoryGroup,
  type UnknownEntry,
} from './integration-state';
import { canonicalCategory } from './legacy-category';
import type {
  FlightDataStatus,
  IntegrationDescriptor,
  IntegrationMode,
  ProviderStatus,
  RoutingMember,
  RoutingView,
} from './types';

/**
 * THE PROVIDERS PAGE AS ROWS — the pure model behind the redesigned screen.
 *
 * The page answers one question per SERVICE ("what does this environment use
 * for hotels, in which mode, and does it answer?"), so the unit of layout is a
 * service card holding a compact table of rows. This module decides which rows
 * exist, in which order, what each one counts as for the summary filters at the
 * top, and what deserves the single attention line. The components only render
 * what it computed.
 *
 * Two facts still never collapse (see `integration-state.ts`):
 *   · a row that is OFF or NOT CONFIGURED — a descriptor answered `enabled:false`,
 *     or FlightAware has no predictor URL — counts under the "off" filter;
 *   · a service we could not ASK (an unreachable declarant, an unreadable
 *     predictor) is not a row at all and never counts as "off". Not knowing is
 *     not the same as knowing it is off.
 */

/** The five summary filters, in the order the page shows them. */
export type ProviderFilter = 'all' | 'real' | 'sandbox' | 'synthetic' | 'off';

export const PROVIDER_FILTERS: readonly ProviderFilter[] = ['all', 'real', 'sandbox', 'synthetic', 'off'];

/** Which wire mode each mode filter selects. "Real" is the vendor's production. */
const FILTER_MODE: Record<Exclude<ProviderFilter, 'all' | 'off'>, IntegrationMode> = {
  real: 'production',
  sandbox: 'sandbox',
  synthetic: 'mock',
};

/** What one row counts as for the filters and the "N in use" summary. */
export interface RowFacts {
  /** The mode it runs in; `null` when nothing runs (not enabled, not configured, unknown). */
  mode: IntegrationMode | null;
  /** Switched off, missing its credentials, not enabled or not configured. Never "unknown". */
  off: boolean;
  /** Carries traffic in this environment right now. */
  inUse: boolean;
}

export function matchesFilter(facts: RowFacts, filter: ProviderFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'off') return facts.off;
  return facts.mode === FILTER_MODE[filter];
}

export function filterCounts(rows: readonly RowFacts[]): Record<ProviderFilter, number> {
  const counts = { all: 0, real: 0, sandbox: 0, synthetic: 0, off: 0 };
  for (const row of rows) {
    for (const filter of PROVIDER_FILTERS) if (matchesFilter(row, filter)) counts[filter] += 1;
  }
  return counts;
}

/** Clicking the active pill clears it; "all" is the cleared state. */
export function nextFilter(current: ProviderFilter, picked: ProviderFilter): ProviderFilter {
  return current === picked ? 'all' : picked;
}

/**
 * Absent credentials are NORMAL on a vendor this instance does not run, and on
 * one that needs none (synthetic, or no auth at all). They are a problem only on
 * a vendor it says it runs.
 */
export function credentialsMissing(
  d: Pick<IntegrationDescriptor, 'enabled' | 'credentialsPresent' | 'mode' | 'effectiveMode' | 'authStrategy'>,
): boolean {
  return d.enabled && !d.credentialsPresent && effectiveMode(d) !== 'mock' && d.authStrategy !== 'none';
}

/** The server's availability verdict for a row (configuration alone when the BFF sent none). */
function serverState(d: IntegrationDescriptor) {
  return (d.health ?? fallbackHealth(d)).state;
}

/**
 * A row the instance built (or one it can switch back on). Counted on the
 * SERVER's verdict, not on a manual probe: the pills must not move because one
 * operator pressed "Test connection" — the probe's re-read brings them in line.
 */
export function integrationFacts(d: IntegrationDescriptor): RowFacts {
  const state = serverState(d);
  const blocked = state === 'disabled' || state === 'no_credentials';
  // M2: the mode IN USE (an operator may have moved it off the boot mode).
  return { mode: effectiveMode(d), off: !d.enabled || blocked, inUse: d.enabled && !blocked };
}

/** A channel this instance does not offer: nothing runs, so it has no mode. */
export const UNWIRED_FACTS: RowFacts = { mode: null, off: true, inUse: false };

/** A routed provider that has no inventory row of its own. */
export function memberFacts(member: RoutingMember): RowFacts {
  return { mode: member.mode ?? null, off: !member.serving, inUse: member.serving && member.enabled };
}

/* ─────────────────────────── Flight information ──────────────────────────── */

/**
 * The FlightAware row (AeroAPI via the flight predictor), from the predictor's
 * freshness answer. `unreadable` (the BFF read failed) and `unreachable` (the
 * predictor did not answer) are UNKNOWN — never "not configured".
 */
export type FlightRowState =
  | 'loading'
  | 'unreadable'
  | 'not_configured'
  | 'unreachable'
  | 'pull_failed'
  | 'never_pulled'
  | 'online';

export function flightRowState(
  data: FlightDataStatus | undefined,
  { isLoading, isError }: { isLoading: boolean; isError: boolean },
): FlightRowState {
  if (!data) return isLoading && !isError ? 'loading' : 'unreadable';
  if (!data.configured) return 'not_configured';
  if (!data.reachable) return 'unreachable';
  if (!data.lastPull) return 'never_pulled';
  return data.lastPull.status === 'error' ? 'pull_failed' : 'online';
}

export function flightFacts(state: FlightRowState): RowFacts {
  // FlightAware has no sandbox: once the predictor is wired, the data is real.
  const configured = state !== 'loading' && state !== 'unreadable' && state !== 'not_configured';
  return {
    mode: configured ? 'production' : null,
    off: state === 'not_configured',
    inUse: configured && state !== 'unreachable',
  };
}

/* ──────────────────────────────── Sections ───────────────────────────────── */

/**
 * Id of the flight-information card. FlightAware (via the predictor) is not an
 * inventory row, so it is the card's `lead` row; the `flight-info` bindings an
 * app declares (the airline's service adapter, M2) join it as ordinary rows —
 * and the airline provider, locked first by the order of use (the airline
 * always wins, 2026-10-29), is drawn above it.
 */
export const FLIGHT_INFO_SECTION = 'flight-info';

/**
 * The order the service cards read in (M2, one per SERVICE): flight
 * information, passenger data, air tickets (seats to re-accommodate onto),
 * hotels, transport, notifications, payments. A legacy spelling reads as its
 * service (`legacy-category.ts`). Any other category follows alphabetically,
 * so a new one appears instead of silently disappearing.
 */
export const SECTION_ORDER: readonly string[] = [
  FLIGHT_INFO_SECTION,
  'passenger-data',
  'air-ticketing',
  'hotel',
  'transport',
  'notifications',
  'wallet',
];

/** One row of a service card. `vendor` is unique within its card. */
export type SectionRow =
  /** The instance built it — or can switch it back on (#839). */
  | { kind: 'integration'; id: string; vendor: string; descriptor: IntegrationDescriptor; facts: RowFacts }
  /** A channel this instance does not offer: a missing reach, named, never collapsed. */
  | { kind: 'unwired'; id: string; vendor: string; descriptor: IntegrationDescriptor; facts: RowFacts }
  /** A routing member the inventory gave no row of its own. */
  | { kind: 'member'; id: string; vendor: string; member: RoutingMember; facts: RowFacts };

export interface ServiceSectionModel {
  category: string;
  group: CategoryGroup;
  /** Unfiltered, in inventory order; the card re-sorts routed members by draft. */
  rows: SectionRow[];
  /** Alternatives this build supports but does not use here — the footer line. */
  others: IntegrationDescriptor[];
  /** The "Order of use" views of this category that have members. */
  routing: RoutingView[];
}

export function buildSections(
  integrations: IntegrationDescriptor[],
  routing: readonly RoutingView[],
): ServiceSectionModel[] {
  const groups = groupByCategory(integrations);
  const known = SECTION_ORDER.flatMap((c) => groups.filter((g) => g.category === c));
  // groupByCategory already sorts the categories it does not know alphabetically.
  const rest = groups.filter((g) => !SECTION_ORDER.includes(g.category));

  return [...known, ...rest].map((group) => {
    const views = routing.filter((v) => canonicalCategory(v.category) === group.category && v.members.length > 0);
    const rows: SectionRow[] = group.wired.map((descriptor) => ({
      kind: 'integration',
      id: integrationKey(descriptor),
      vendor: descriptor.vendor,
      descriptor,
      facts: integrationFacts(descriptor),
    }));
    let others: IntegrationDescriptor[] = [];
    if (group.collapseUnwired) {
      others = group.unwired;
    } else {
      for (const descriptor of group.unwired) {
        rows.push({ kind: 'unwired', id: integrationKey(descriptor), vendor: descriptor.vendor, descriptor, facts: UNWIRED_FACTS });
      }
    }
    // A routed provider is always a row, so its order controls have a place to live.
    const seen = new Set(rows.map((r) => r.vendor));
    for (const view of views) {
      for (const member of view.members) {
        if (seen.has(member.vendor)) continue;
        seen.add(member.vendor);
        rows.push({
          kind: 'member',
          id: integrationKey({ category: group.category, vendor: member.vendor }),
          vendor: member.vendor,
          member,
          facts: memberFacts(member),
        });
      }
    }
    others = others.filter((d) => !seen.has(d.vendor));
    return { category: group.category, group, rows, others, routing: views };
  });
}

/** A service with nothing declared — the flight-information card before any binding exists. */
export function emptySection(category: string): ServiceSectionModel {
  const selection = categorySelection(category);
  return {
    category,
    group: { category, selection, wired: [], unwired: [], collapseUnwired: selection !== 'channels' },
    rows: [],
    others: [],
    routing: [],
  };
}

/* ───────────────────────────── Vendor accounts ───────────────────────────── */

/** Whether the account's bindings hold their credential (D15: one set per vendor). */
export type AccountCredentials = 'present' | 'partial' | 'missing' | 'absent';

/**
 * ONE vendor account providing N services (D1). The account is WHO (credential,
 * contract, quota); each service it provides is its own binding — a row in that
 * service's card.
 */
export interface VendorAccount {
  vendor: string;
  /** Canonical services, in the page's order. */
  services: string[];
  credentials: AccountCredentials;
  /** The services whose binding does not hold the credential. */
  lacking: string[];
}

function sectionRank(category: string): number {
  const i = SECTION_ORDER.indexOf(category);
  return i < 0 ? SECTION_ORDER.length : i;
}

/**
 * The vendors that serve TWO OR MORE services here (Sabre: hotels + air
 * tickets), so the page can say each account once. A vendor that holds no
 * credential at all (`authStrategy: none` — the in-platform synthetic
 * providers, the seeded schedule) is not an account. Missing is only a problem
 * on a binding this instance runs ({@link credentialsMissing}).
 */
export function vendorAccounts(integrations: readonly IntegrationDescriptor[]): VendorAccount[] {
  const byVendor = new Map<string, Map<string, IntegrationDescriptor>>();
  for (const d of integrations) {
    if (d.authStrategy === 'none') continue;
    const services = byVendor.get(d.vendor) ?? new Map<string, IntegrationDescriptor>();
    services.set(canonicalCategory(d.category), d);
    byVendor.set(d.vendor, services);
  }
  const accounts: VendorAccount[] = [];
  for (const [vendor, bindings] of [...byVendor].sort(([a], [b]) => a.localeCompare(b))) {
    if (bindings.size < 2) continue;
    const services = [...bindings.keys()].sort((a, b) => sectionRank(a) - sectionRank(b) || a.localeCompare(b));
    const lacking = services.filter((s) => !bindings.get(s)?.credentialsPresent);
    const credentials: AccountCredentials =
      lacking.length === 0
        ? 'present'
        : [...bindings.values()].some(credentialsMissing)
          ? 'missing'
          : lacking.length < services.length
            ? 'partial'
            : 'absent';
    accounts.push({ vendor, services, credentials, lacking });
  }
  return accounts;
}

/** Routed rows first, in `order` (the draft being edited); the rest keep their place. */
export function orderRows<T extends { vendor: string }>(rows: readonly T[], order: readonly string[]): T[] {
  const rank = new Map(order.map((vendor, i) => [vendor, i] as const));
  const ranked = rows
    .filter((r) => rank.has(r.vendor))
    .sort((a, b) => (rank.get(a.vendor) ?? 0) - (rank.get(b.vendor) ?? 0));
  return [...ranked, ...rows.filter((r) => !rank.has(r.vendor))];
}

/**
 * The configured egress limit of ONE vendor, from the recent-traffic read. The
 * BFF reports it per logical provider as `"<vendor> <method> · <rate>"` joined by
 * `; `; only the segments naming this vendor belong to its row.
 */
export function vendorRateLimit(vendor: string, traffic: readonly ProviderStatus[] | undefined): string | undefined {
  const prefix = `${vendor.toLowerCase()} `;
  const found = new Set<string>();
  for (const provider of traffic ?? []) {
    for (const segment of provider.rateLimit?.configured?.split(';') ?? []) {
      const trimmed = segment.trim();
      if (trimmed.toLowerCase().startsWith(prefix)) found.add(trimmed.slice(prefix.length));
    }
  }
  return found.size ? [...found].join('; ') : undefined;
}

/* ─────────────────────────────── Attention ───────────────────────────────── */

/** One thing the operator should look at — said once, in the line under the pills. */
export type AttentionItem =
  | { kind: 'unknown'; declaredBy: string }
  | { kind: 'flight'; state: 'not_configured' | 'unreachable' | 'unreadable' }
  | { kind: 'offline'; descriptor: IntegrationDescriptor }
  | { kind: 'credentials'; descriptor: IntegrationDescriptor };

/**
 * What needs attention, most likely outage first: a service we could not ask,
 * the flight source, a provider in use that did not answer, then one in use
 * that is missing its credentials. Empty → the line is not shown at all.
 */
export function attentionItems(args: {
  integrations: readonly IntegrationDescriptor[];
  unknown: readonly UnknownEntry[];
  flight: FlightRowState;
}): AttentionItem[] {
  const items: AttentionItem[] = args.unknown.map((u) => ({ kind: 'unknown', declaredBy: u.declaredBy }));
  if (args.flight === 'not_configured' || args.flight === 'unreachable' || args.flight === 'unreadable') {
    items.push({ kind: 'flight', state: args.flight });
  }
  for (const descriptor of args.integrations) {
    if (descriptor.enabled && serverState(descriptor) === 'offline') items.push({ kind: 'offline', descriptor });
  }
  for (const descriptor of args.integrations) {
    if (!descriptor.enabled) continue;
    if (serverState(descriptor) === 'no_credentials' || credentialsMissing(descriptor)) {
      items.push({ kind: 'credentials', descriptor });
    }
  }
  return items;
}
